import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { showResult } from 'infrastructure/hooks/utils';

import s from './styles.module.scss';

const ACCEPTED_MIME = 'application/pdf';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export type PapFormUploadMode = 'create' | 'edit';

export interface PapFormUploadValues {
  file?: File;
  formName?: string;
  notes?: string;
}

interface PapFormUploadModalProps {
  open: boolean;
  mode?: PapFormUploadMode;
  title?: string;
  submitting?: boolean;
  initialFormName?: string;
  initialNotes?: string;
  initialFile?: File | null;
  currentFileName?: string;
  formNameError?: string;
  onCancel: () => void;
  onSubmit: (values: PapFormUploadValues) => Promise<void> | void;
}

const PapFormUploadModal: React.FC<PapFormUploadModalProps> = ({
  open,
  mode = 'create',
  title,
  submitting,
  initialFormName,
  initialNotes,
  initialFile,
  currentFileName,
  formNameError,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [formName, setFormName] = useState<string>(initialFormName ?? '');
  const [notes, setNotes] = useState<string>(initialNotes ?? '');
  const [isReplacingFile, setIsReplacingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<{
    formName?: string;
    file?: string;
  }>({});
  const dragCounterRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setFile(initialFile ?? null);
    setFormName(initialFormName ?? '');
    setNotes(initialNotes ?? '');
    setIsReplacingFile(false);
    setIsSubmitting(false);
    setIsDragging(false);
    setErrors({});
    dragCounterRef.current = 0;
  }, [open, initialFile, initialFormName, initialNotes]);

  useEffect(() => {
    if (formNameError) {
      setErrors((prev) => ({ ...prev, formName: formNameError }));
    }
  }, [formNameError]);

  const resetNativeInput = (input?: HTMLInputElement | null) => {
    if (input) input.value = '';
  };

  const validateFile = (
    f?: File | null,
  ): { file: File | null; error?: string } => {
    if (!f) return { file: null };

    const isPdf =
      f.type === ACCEPTED_MIME || f.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      const error = t(
        'messages.papFormInvalidType',
        'Only PDF files up to 10MB are supported',
      );
      return { file: null, error };
    }

    if (f.size > MAX_FILE_SIZE_BYTES) {
      const error = t(
        'messages.papFormFileTooLarge',
        `File size must be ${MAX_FILE_SIZE_MB}MB or less.`,
      );
      return { file: null, error };
    }

    return { file: f };
  };

  const onPickFileClick = () => {
    if (isLoading) return;
    fileInputRef.current?.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { file: validatedFile, error } = validateFile(e.target.files?.[0]);
    resetNativeInput(e.target);

    if (error) {
      setErrors((prev) => ({ ...prev, file: error }));
      return;
    }

    if (!validatedFile) return;

    setFile(validatedFile);
    setFormName((prev) => (prev?.trim() ? prev : validatedFile.name));
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleFileDrop = (droppedFile: File) => {
    const { file: validatedFile, error } = validateFile(droppedFile);

    if (error) {
      setErrors((prev) => ({ ...prev, file: error }));
      return;
    }

    if (!validatedFile) return;

    setFile(validatedFile);
    setFormName((prev) => (prev?.trim() ? prev : validatedFile.name));
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    if (isLoading) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileDrop(droppedFiles[0]);
    }
  };

  const isLoading = submitting || isSubmitting;

  useEffect(() => {
    if (!open) return;

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [open, isLoading, onCancel]);

  const handleRemoveFile = () => {
    if (isLoading) return;

    if (mode === 'create') {
      setFile(null);
      resetNativeInput(fileInputRef.current);
      return;
    }

    if (!isReplacingFile) {
      setIsReplacingFile(true);
      setFile(null);
      resetNativeInput(fileInputRef.current);
      return;
    }
    setFile(null);
    resetNativeInput(fileInputRef.current);
  };

  const displayFileName = useMemo(() => {
    if (mode === 'edit') {
      if (!isReplacingFile) {
        return (
          currentFileName ?? t('labels.noFileSelected', 'No file selected')
        );
      }
      return file?.name ?? t('labels.noFileSelected', 'No file selected');
    }
    return file?.name ?? t('labels.noFileSelected', 'No file selected');
  }, [mode, isReplacingFile, file?.name, currentFileName, t]);

  const primaryLabel = useMemo(() => {
    return mode === 'edit'
      ? t('controls.save', 'Save')
      : t('controls.upload', 'Upload');
  }, [mode, t]);

  const showChooseButton = useMemo(() => {
    if (isLoading) return false;

    if (mode === 'create') return !file;

    if (mode === 'edit') return isReplacingFile && !file;

    return false;
  }, [isLoading, mode, isReplacingFile, file]);

  const showXButton = useMemo(() => {
    if (isLoading) return false;

    if (mode === 'create') return !!file;

    if (mode === 'edit') {
      if (!isReplacingFile) return !!currentFileName;
      return !!file;
    }

    return false;
  }, [isLoading, mode, isReplacingFile, currentFileName, file]);

  const handleSubmit = async () => {
    const trimmedName = formName?.trim();
    const trimmedNotes = notes?.trim();
    const newErrors: { formName?: string; file?: string } = {};

    if (!trimmedName && mode === 'create') {
      newErrors.formName = t(
        'messages.formNameRequired',
        'Form name is required',
      );
    } else if (trimmedName && trimmedName.length > 255) {
      newErrors.formName = t(
        'messages.formNameTooLong',
        'Form name must be 255 characters or less',
      );
    }

    if (trimmedNotes && trimmedNotes.length > 1000) {
      showResult(
        t('messages.notesTooLong', 'Notes must be 1000 characters or less'),
      );
      return;
    }

    if (mode === 'create') {
      if (!file) {
        newErrors.file = t(
          'messages.papFormFileRequired',
          'PDF file is required',
        );
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit({
          file: file ?? undefined,
          formName: trimmedName || file!.name,
          notes: trimmedNotes || undefined,
        });
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    if (mode === 'edit' && isReplacingFile && !file) {
      newErrors.file = t(
        'messages.papFormFileRequired',
        'PDF file is required',
      );
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        file: isReplacingFile ? file ?? undefined : undefined,
        formName: trimmedName || undefined,
        notes: trimmedNotes || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldRenderFileInput = mode === 'create' || isReplacingFile;

  return (
    <Modal
      open={open}
      title={
        title ??
        (mode === 'edit'
          ? t('labels.editPapForm', 'Edit PAP Form')
          : t('labels.uploadPapForm'))
      }
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={!isLoading}
      closable={!isLoading}
    >
      <div className={s.body}>
        <div className={s.row}>
          <p className={s.label}>
            {t('labels.formName', 'Form Name')}
            <span className={s.required}> *</span>
          </p>
          <Input
            value={formName}
            onChange={(e) => {
              setFormName(e.target.value);
              if (errors.formName) {
                setErrors((prev) => ({ ...prev, formName: undefined }));
              }
            }}
            placeholder={t('placeholders.formName', 'Enter form name')}
            disabled={isLoading}
            maxLength={255}
            status={errors.formName ? 'error' : undefined}
          />
          {errors.formName && (
            <span className={s.errorText}>{errors.formName}</span>
          )}
          <span className={s.helperText}>
            {formName.length}/255 {t('labels.characters', 'characters')}
          </span>
        </div>

        <div className={s.row}>
          <p className={s.label}>{t('labels.notes', 'Notes')}</p>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('placeholders.notes', 'Optional notes')}
            autoSize={{ minRows: 3, maxRows: 6 }}
            disabled={isLoading}
            maxLength={1000}
          />
          <span className={s.helperText}>
            {notes.length}/1000 {t('labels.characters', 'characters')}
          </span>
        </div>

        <div className={s.row}>
          <p className={s.label}>
            {t('labels.file', 'File')}
            <span className={s.required}> *</span>
          </p>
          {showChooseButton ? (
            <div
              className={`${s.dropZone} ${isDragging ? s.dragging : ''} ${
                errors.file ? s.error : ''
              }`}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={onPickFileClick}
            >
              <p className={s.dropZoneText}>
                {isDragging
                  ? t('labels.dropFileHere', 'Drop file here')
                  : t(
                      'labels.dragDropOrClick',
                      'Drag & drop PDF file here, or click to browse',
                    )}
              </p>
              <p className={s.dropZoneSubtext}>
                {t('labels.pdfOnly10MB', 'PDF only, max 10MB')}
              </p>
            </div>
          ) : (
            <div className={s.fileRow}>
              <span className={s.fileName}>{displayFileName}</span>

              {showXButton && (
                <button
                  type="button"
                  className={s.removeFile}
                  onClick={handleRemoveFile}
                  disabled={isLoading}
                  aria-label={t('controls.changeFile', 'Change file')}
                >
                  <CloseOutlined />
                </button>
              )}
            </div>
          )}

          {errors.file && <span className={s.errorText}>{errors.file}</span>}
        </div>

        <div className={s.footer}>
          <BaseButton
            type="secondary"
            label={t('controls.cancel', 'Cancel')}
            onClick={onCancel}
            disabled={isLoading}
          />
          <BaseButton
            type="primary"
            label={primaryLabel}
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>

      {shouldRenderFileInput && (
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="application/pdf"
          onChange={onFileChange}
        />
      )}
    </Modal>
  );
};

export default PapFormUploadModal;
